/**
 * Likevekt (Binairo/Takuzu) - Type Definitions
 *
 * A puzzle game where you fill a grid with black and white cells following three rules:
 * 1. No more than two consecutive same-colored cells in any row/column
 * 2. Each row/column must have equal numbers of black and white cells
 * 3. All rows must be unique, all columns must be unique
 */

/**
 * Cell value - 0 = empty, 1 = black, 2 = white
 */
export type CellValue = 0 | 1 | 2;

export const EMPTY: CellValue = 0;
export const BLACK: CellValue = 1;
export const WHITE: CellValue = 2;

// Legacy aliases for compatibility
export const X = BLACK;
export const O = WHITE;

/**
 * Get the opposite color
 */
export function opposite(value: CellValue): CellValue {
  if (value === BLACK) return WHITE;
  if (value === WHITE) return BLACK;
  return EMPTY;
}

/**
 * Cell type - whether it's a given clue or player-placed
 */
export type CellType = 'given' | 'player';

/**
 * Difficulty levels
 */
export type Difficulty = 'easy' | 'medium' | 'hard' | 'expert';

/**
 * Supported grid sizes (must be even)
 */
export type GridSize = 6 | 8 | 10 | 12 | 14;

/**
 * Cell flags (bitwise)
 */
export const CellFlags = {
  IMMUTABLE: 0x0001, // Given clue (cannot be edited)
  ERROR: 0x0002, // Cell is part of a rule violation
  ERROR_ROW_TRIPLE: 0x0004, // Part of triple in row
  ERROR_COL_TRIPLE: 0x0008, // Part of triple in column
  ERROR_ROW_COUNT: 0x0010, // Row has wrong count
  ERROR_COL_COUNT: 0x0020, // Column has wrong count
  ERROR_ROW_DUPLICATE: 0x0040, // Row is duplicate
  ERROR_COL_DUPLICATE: 0x0080, // Column is duplicate
} as const;

/**
 * Puzzle preset for UI
 */
export interface PuzzlePreset {
  name: string;
  size: GridSize;
  difficulty: Difficulty;
}

/**
 * Standard presets
 */
export const PRESETS: PuzzlePreset[] = [
  { name: '6x6 Easy', size: 6, difficulty: 'easy' },
  { name: '6x6 Medium', size: 6, difficulty: 'medium' },
  { name: '8x8 Easy', size: 8, difficulty: 'easy' },
  { name: '8x8 Medium', size: 8, difficulty: 'medium' },
  { name: '8x8 Hard', size: 8, difficulty: 'hard' },
  { name: '10x10 Medium', size: 10, difficulty: 'medium' },
  { name: '10x10 Hard', size: 10, difficulty: 'hard' },
  { name: '10x10 Expert', size: 10, difficulty: 'expert' },
];

/**
 * Game state
 */
export interface GameState {
  size: GridSize;
  difficulty: Difficulty;
  completed: boolean;
  cheated: boolean; // Used hints

  // Grid data (size × size arrays)
  cells: CellValue[]; // Current cell values
  flags: number[]; // Cell flags (immutable, errors)

  // Solution for validation
  solution: CellValue[];

  // Selection state
  selectedX: number;
  selectedY: number;
}

/**
 * Solver technique names for difficulty tracking
 */
export type SolverTechnique =
  | 'blockAdjacentPairs'
  | 'blockSandwich'
  | 'completeByCount'
  | 'anticipateTriples'
  | 'avoidDuplicates'
  | 'backtracking';

/**
 * Solver result
 */
export interface SolverResult {
  status: 'solved' | 'no_solution' | 'multiple_solutions' | 'stuck';
  solution?: CellValue[];
  solutionCount?: number;
  techniquesUsed: SolverTechnique[];
}

/**
 * Hint result
 */
export interface HintResult {
  x: number;
  y: number;
  value: CellValue;
  technique: string;
  explanation: string;
}

/**
 * Validation error
 */
export interface ValidationError {
  type: 'triple' | 'count' | 'duplicate';
  direction: 'row' | 'column';
  index: number;
  cells: Array<{ x: number; y: number }>;
}

/**
 * Generator options
 */
export interface GeneratorOptions {
  size: GridSize;
  difficulty: Difficulty;
  seed?: number;
  timeout?: number;
}

/**
 * Generated puzzle
 */
export interface GeneratedPuzzle {
  size: GridSize;
  difficulty: Difficulty;
  givens: Array<{ x: number; y: number; value: CellValue }>;
  solution: CellValue[];
  givenCount: number;
  seed: number;
}

/**
 * Mulberry32 PRNG - fast, simple, seedable
 */
export function seededRandom(seed: number): () => number {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Shuffle array using Fisher-Yates
 */
export function shuffle<T>(array: T[], rng: () => number): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [result[i], result[j]] = [result[j]!, result[i]!];
  }
  return result;
}
